/*
 * PROD-7828 R5 — pure formatting/predicate helpers for the Teacher Payments module.
 * I/O-free on purpose: every branch here is unit-tested (format.test.js) and the
 * components stay thin renderers.
 */

export const EM_DASH = '—';

// A wire number that may be missing or malformed; the view shows 0 rather than NaN.
export const num = (v) => {
  const n = Number(v);
  return v === null || v === undefined || v === '' || !Number.isFinite(n) ? 0 : n;
};

// ₹ amounts, en-IN grouping. Whole rupees by default (L1); L2 breakdown passes decimals: 2
// because D-3 requires the unrounded net there.
export const fmt = (n, decimals = 0) => {
  const value = Number(n);
  if (n === null || n === undefined || n === '' || !Number.isFinite(value)) return EM_DASH;
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value).toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return `${sign}₹${abs}`;
};

export const fmt_signed = (n, decimals = 0) => {
  const value = Number(n);
  if (n === null || n === undefined || n === '' || !Number.isFinite(value)) return EM_DASH;
  return value >= 0 ? `+${fmt(value, decimals)}` : fmt(value, decimals);
};

export const fmt_count = (n) => {
  const value = Number(n);
  return Number.isFinite(value) ? value.toLocaleString('en-IN') : EM_DASH;
};

// A month row is "needs review" when the engine could not finish (compute_error) or the
// stored numbers are known-stale (needs_recalc). Such rows must NEVER show an amount (D-11
// validation instrument: a stale number on screen is worse than no number).
export const needs_review = (row) => {
  if (!row) return false;
  if (row.needs_review === true) return true;
  if (row.compute_error !== null && row.compute_error !== undefined && row.compute_error !== '') {
    return true;
  }
  return row.needs_recalc === 1 || row.needs_recalc === true;
};

export const needs_review_reason = (row) => {
  if (!row) return '';
  if (row.compute_error) return String(row.compute_error);
  if (row.needs_recalc === 1 || row.needs_recalc === true) {
    return 'Marked for recalculation — the stored amounts are stale.';
  }
  return '';
};

// 'YYYY-MM-DD' (or an ISO timestamp) -> 'June 2026'. Parsed as plain text, not Date, so a
// UTC-midnight pay_month never slips to the previous month in a negative-offset browser.
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const month_label = (pay_month) => {
  if (!pay_month || typeof pay_month !== 'string') return '';
  const m = pay_month.match(/^(\d{4})-(\d{2})/);
  if (!m) return pay_month;
  const idx = Number(m[2]) - 1;
  if (idx < 0 || idx > 11) return pay_month;
  return `${MONTH_NAMES[idx]} ${m[1]}`;
};

// Tie-out export filename (D-12). Stable, sortable, and collision-safe across same-day runs.
export const csv_filename = (pay_month, now = new Date()) => {
  const month_part = (typeof pay_month === 'string' && pay_month.slice(0, 7)) || 'all';
  const pad = (v) => String(v).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(
    now.getHours()
  )}${pad(now.getMinutes())}`;
  return `teacher_payments_overview_${month_part}_${stamp}.csv`;
};

// The CSV endpoint may answer as raw text/csv or wrapped in the house envelope
// ({status, data:{csv}}). Accept both so a backend shape change doesn't break the export.
export const extract_csv_payload = (body) => {
  if (typeof body === 'string') return body;
  if (!body || typeof body !== 'object') return '';
  if (typeof body.csv === 'string') return body.csv;
  const data = body.data;
  if (typeof data === 'string') return data;
  if (data && typeof data.csv === 'string') return data.csv;
  return '';
};

// The module is grant-only end to end (A6): a user without TEACHER_OPS_PAYMENT gets 403 on
// every read, which must render the clean no-permission state instead of an error toast.
export const is_permission_error = (e) => {
  const status = e && e.response && e.response.status;
  return status === 403 || status === 401;
};

const text_or_empty = (v) => (typeof v === 'string' ? v : '');

// CD-128, the module's one error reader: the code is error_code, else R8's reason (its code before
// error_code existed); the text is always message (R5b's reason is prose, R8's is a code).
export const api_error = (e) => {
  const body = (e && e.response && e.response.data) || {};
  return {
    code: text_or_empty(body.error_code) || text_or_empty(body.reason),
    message: text_or_empty(body.message)
  };
};

export const error_message = (e, fallback) => api_error(e).message || fallback;

// CD-144: a LOOKUP_FAILED body carries its own Ops sentence — show it as-is (never the raw code);
// any other failure (network, other 4xx/5xx) falls back to the tab's own wording.
export const load_failed_text = (e, fallback) => {
  const { code, message } = api_error(e);
  return (code === 'LOOKUP_FAILED' && message.replace(/\s*try again\.?\s*$/i, '')) || fallback;
};

// House envelope is {status:'success', data:{...}} on the goserver /v2 routes.
export const envelope_data = (res) => (res && res.data && res.data.data) || {};

// CD-237 (D100): report totals are summed unrounded and rounded once; the reports and rules pages say so.
export const TOTALS_ROUNDING_NOTE =
  'Totals are added before rounding, so the rows may differ from the total by a paisa.';
